/**
 * Chat action contract (backend `app/services/actions`, `app/services/booking`): the `action`
 * stream event for `book_tour` / `lead_form`, the slots response, the confirm request/response and
 * the result card. Decoded identically by `@sagot/chat`, the widget panel and the mobile app -
 * `contracts/chat-actions.json` holds shared fixtures every decoder is tested against.
 *
 * Flow: the server creates a pending run when it offers a card (`run_id`, `expires_at`), the card
 * loads slots (`GET /public/v1/actions/{run_id}/slots`) and confirms
 * (`POST /public/v1/actions/{run_id}/confirm`); the response is the result card, which is also
 * stored in the conversation and restored on reload (`state: "succeeded"` + `result`).
 */
import { z } from "zod";

/** Optional value the server may send as `null` (Pydantic `None`): null → undefined. */
function nullable<T extends z.ZodTypeAny>(schema: T) {
  return schema.nullish().transform((value): z.output<T> | undefined => value ?? undefined);
}

/** `checkboxes`: pick any number of `options`; the answer travels as one line per choice. */
export const FIELD_TYPES = ["text", "email", "tel", "select", "textarea", "checkboxes"] as const;
export type FieldType = (typeof FIELD_TYPES)[number];
/** A `checkboxes` answer in the confirm request: one ticked choice per line (backend `MULTI_CHOICE_SEPARATOR`). */
export const MULTI_CHOICE_SEPARATOR = "\n";

/** The ticked choices of a `checkboxes` answer, in the field's option order. */
export function splitChoices(value: string, options: readonly string[] = []): string[] {
  const picked = new Set(value.split(MULTI_CHOICE_SEPARATOR).map((choice) => choice.trim()));
  return options.filter((option) => picked.has(option));
}

export const actionField = z.object({
  name: z.string(),
  label: z.string(),
  type: z.enum(FIELD_TYPES).catch("text"),
  required: z.boolean().catch(false),
  max_length: nullable(z.number().int().positive()),
  options: nullable(z.array(z.string())),
  /** Display text for machine-style option values (`day_tour` → "Day tour"). */
  option_labels: nullable(z.record(z.string())),
  autocomplete: nullable(z.string()),
  /** Pre-filled from what the visitor already said (never stored server-side). */
  value: nullable(z.string()),
});
export type ActionField = z.output<typeof actionField>;

export const CARD_STATES = ["pending", "succeeded", "expired", "unavailable"] as const;
export type CardState = (typeof CARD_STATES)[number];

export const resultCard = z.object({
  kind: z.enum(["lead", "booking", "custom", "failed"]),
  /** `sent` (lead) · `requested` / `confirmed` (booking) · `done` (custom action) · `failed`. */
  state: z.string(),
  title: z.string(),
  body: z.string(),
  owner: nullable(z.string()),
  start: nullable(z.string()),
  end: nullable(z.string()),
  timezone: nullable(z.string()),
  location: nullable(z.string()),
  meet_url: nullable(z.string()),
  calendar_url: nullable(z.string()),
  /** iCalendar file content (confirmed bookings only): "Add to calendar". */
  ics: nullable(z.string()),
});
export type ResultCard = z.output<typeof resultCard>;

const cardState = z.enum(CARD_STATES).catch("pending");

const common = {
  action_id: z.string(),
  run_id: nullable(z.string()),
  expires_at: nullable(z.string()),
  title: z.string(),
  consent_text: nullable(z.string()),
  privacy_url: nullable(z.string()),
  submit_label: nullable(z.string()),
  /** Present on restored cards (`GET /public/v1/conversations/{id}`); live offers are `pending`. */
  state: cardState.optional().transform((state) => state ?? "pending"),
  result: nullable(resultCard),
};

export const BOOKING_MODES = ["calendar", "request"] as const;
export type BookingMode = (typeof BOOKING_MODES)[number];

export const tourSlot = z.object({ id: z.string(), label: z.string().optional(), start: z.string().optional(), end: z.string().optional() });
export type TourSlot = z.output<typeof tourSlot>;

export const bookTourPayload = z.object({
  ...common,
  integration: nullable(z.string()),
  mode: z.enum(BOOKING_MODES).catch("request"),
  /** The business time zone (display hint); the picker groups days in the visitor's own zone. */
  timezone: nullable(z.string()),
  duration_minutes: nullable(z.number().int().positive()),
  location: nullable(z.string()),
  /** Always empty today: the picker loads slots from the slots endpoint. */
  slots: z.array(tourSlot).catch([]),
  fields: z.array(actionField).catch([]),
  consent_text: z.string().catch(""),
});
export type BookTourPayload = z.output<typeof bookTourPayload>;

export const leadFormPayload = z.object({
  ...common,
  fields: z.array(actionField),
  /** `email_or_phone`: at least one of the two must be filled. */
  contact_rule: nullable(z.string()),
  consent_text: z.string().catch(""),
});
export type LeadFormPayload = z.output<typeof leadFormPayload>;

/** `GET /public/v1/actions/{run_id}/slots` */
export const slot = z.object({ id: z.string(), start: z.string(), end: z.string() });
export type Slot = z.output<typeof slot>;
export const slotsResponse = z.object({
  mode: z.enum(BOOKING_MODES).catch("request"),
  /** The zone the days are grouped in (the visitor's). */
  timezone: z.string(),
  business_timezone: z.string(),
  duration_minutes: z.number().int().positive(),
  location: nullable(z.string()),
  /** First / last visitor-local dates that can be booked at all (notice, horizon). */
  earliest: z.string(),
  latest: z.string(),
  days: z.array(z.object({ date: z.string(), slots: z.array(slot) })),
  /** The first open slot at/after the range start - only set when the range is empty. */
  next_available: nullable(slot),
});
export type SlotsResponse = z.output<typeof slotsResponse>;

/** `POST /public/v1/actions/{run_id}/confirm` */
export interface ConfirmRequest {
  slot_id?: string;
  arguments?: Record<string, string | number | boolean>;
  consent: boolean;
  /** Same key on a retry returns the first result instead of failing as "already done". */
  idempotency_key: string;
  /** The visitor's IANA time zone. */
  timezone: string;
  turnstile_token?: string;
}
export const confirmResponse = z.object({
  status: z.string(),
  run_id: nullable(z.string()),
  title: z.string(),
  body: z.string(),
  result: nullable(resultCard),
});
export type ConfirmResponse = z.output<typeof confirmResponse>;

/** Error codes the cards react to (`{error: {code}}` of the API envelope). */
export const ACTION_ERROR_CODES = {
  slotUnavailable: "slot_unavailable",
  expired: "action_expired",
  validation: "validation_error",
  captchaRequired: "captcha_required",
  captchaFailed: "captcha_failed",
  rateLimited: "rate_limited",
  unavailable: "service_unavailable",
  authRequired: "authentication_required",
} as const;

/** Max slots Messenger shows as quick replies (Meta limit). */
export const MESSENGER_QUICK_REPLIES_MAX = 13;
/** Days requested per "load more" step in the slot picker (server maximum: 14). */
export const SLOT_PAGE_DAYS = 7;
export const SLOTS_MAX_RANGE_DAYS = 14;
export const ICS_FILENAME = "appointment.ics";

export function decodeBookTour(payload: unknown) {
  return bookTourPayload.safeParse(payload);
}

export function decodeLeadForm(payload: unknown) {
  return leadFormPayload.safeParse(payload);
}

export function decodeSlots(payload: unknown) {
  return slotsResponse.safeParse(payload);
}

export function decodeConfirm(payload: unknown) {
  return confirmResponse.safeParse(payload);
}
